import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { 
  useHospitalProfile, 
  useHospitalDashboard, 
  useUpdateHospitalProfile,
  useHospitalAnalytics 
} from '../../hooks/useHospital';
import { 
  useFleetProfile, 
  useFleetDashboard, 
  useFleetDrivers,
  useAddDriver 
} from '../../hooks/useFleet';
import { 
  useBookings, 
  useCreateBooking, 
  useBookingAnalytics 
} from '../../hooks/useBooking';
import { useAuth } from '../../contexts/AuthContext';
import { hospitalService, fleetService, bookingService } from '../../services';

const ApiIntegrationExample = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('hospital');
  const [formData, setFormData] = useState({});

  // Hospital hooks
  const { data: hospitalProfile, loading: profileLoading } = useHospitalProfile();
  const { data: hospitalDashboard, loading: dashboardLoading } = useHospitalDashboard();
  const { mutate: updateProfile, loading: updateLoading } = useUpdateHospitalProfile();
  const { data: hospitalAnalytics } = useHospitalAnalytics();

  // Fleet hooks
  const { data: fleetProfile, loading: fleetProfileLoading } = useFleetProfile();
  const { data: fleetDashboard, loading: fleetDashboardLoading } = useFleetDashboard();
  const { data: drivers, loading: driversLoading, loadMore: loadMoreDrivers } = useFleetDrivers();
  const { mutate: addDriver, loading: addingDriver } = useAddDriver();

  // Booking hooks
  const { data: bookings, loading: bookingsLoading } = useBookings();
  const { mutate: createBooking, loading: creatingBooking } = useCreateBooking();
  const { data: bookingAnalytics } = useBookingAnalytics();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateProfile = async () => {
    try {
      await updateProfile(hospitalService.updateMyProfile, formData);
      alert('Profile updated successfully!');
      setFormData({});
    } catch (error) {
      alert('Error updating profile: ' + error.message);
    }
  };

  const handleAddDriver = async () => {
    try {
      await addDriver(fleetService.addDriver, formData);
      alert('Driver added successfully!');
      setFormData({});
    } catch (error) {
      alert('Error adding driver: ' + error.message);
    }
  };

  const handleCreateBooking = async () => {
    try {
      await createBooking(bookingService.createBooking, formData);
      alert('Booking created successfully!');
      setFormData({});
    } catch (error) {
      alert('Error creating booking: ' + error.message);
    }
  };

  if (!isAuthenticated) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle>API Integration Example</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Please login to see the API integration examples.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>🚑 Smart Ambulance API Integration Examples</CardTitle>
          <p className="text-sm text-gray-600">
            This component demonstrates how to use the API services and React hooks
            for seamless backend integration.
          </p>
        </CardHeader>
      </Card>

      {/* Tab Navigation */}
      <div className="flex space-x-4 border-b">
        {['hospital', 'fleet', 'booking'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize ${
              activeTab === tab
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab} Management
          </button>
        ))}
      </div>

      {/* Hospital Tab */}
      {activeTab === 'hospital' && (
        <div className="space-y-6">
          {/* Hospital Profile */}
          <Card>
            <CardHeader>
              <CardTitle>Hospital Profile</CardTitle>
            </CardHeader>
            <CardContent>
              {profileLoading ? (
                <div>Loading profile...</div>
              ) : hospitalProfile ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold">{hospitalProfile.data?.name}</h3>
                    <p className="text-sm text-gray-600">{hospitalProfile.data?.address}</p>
                    <p className="text-sm text-gray-600">
                      {hospitalProfile.data?.city}, {hospitalProfile.data?.state} - {hospitalProfile.data?.pincode}
                    </p>
                  </div>
                  
                  {/* Update Profile Form */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Update Profile</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        name="name"
                        placeholder="Hospital Name"
                        value={formData.name || ''}
                        onChange={handleInputChange}
                      />
                      <Input
                        name="address"
                        placeholder="Address"
                        value={formData.address || ''}
                        onChange={handleInputChange}
                      />
                      <Input
                        name="city"
                        placeholder="City"
                        value={formData.city || ''}
                        onChange={handleInputChange}
                      />
                      <Input
                        name="state"
                        placeholder="State"
                        value={formData.state || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Button 
                      onClick={handleUpdateProfile}
                      disabled={updateLoading}
                      className="mt-4"
                    >
                      {updateLoading ? 'Updating...' : 'Update Profile'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div>No profile data available</div>
              )}
            </CardContent>
          </Card>

          {/* Hospital Dashboard */}
          <Card>
            <CardHeader>
              <CardTitle>Hospital Dashboard</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardLoading ? (
                <div>Loading dashboard...</div>
              ) : hospitalDashboard ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800">Total Bookings</h4>
                    <p className="text-2xl font-bold text-blue-600">
                      {hospitalDashboard.data?.statistics?.total_bookings || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800">Revenue</h4>
                    <p className="text-2xl font-bold text-green-600">
                      ₹{hospitalDashboard.data?.statistics?.total_revenue || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-800">Active Bookings</h4>
                    <p className="text-2xl font-bold text-purple-600">
                      {hospitalDashboard.data?.statistics?.active_bookings || 0}
                    </p>
                  </div>
                </div>
              ) : (
                <div>No dashboard data available</div>
              )}
            </CardContent>
          </Card>

          {/* Hospital Analytics */}
          {hospitalAnalytics && (
            <Card>
              <CardHeader>
                <CardTitle>Hospital Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold">Performance Metrics</h4>
                    <ul className="text-sm space-y-1">
                      <li>Response Time: {hospitalAnalytics.data?.avg_response_time || 'N/A'} min</li>
                      <li>Completion Rate: {hospitalAnalytics.data?.completion_rate || 'N/A'}%</li>
                      <li>Patient Satisfaction: {hospitalAnalytics.data?.satisfaction_score || 'N/A'}/5</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold">Recent Trends</h4>
                    <ul className="text-sm space-y-1">
                      <li>Weekly Growth: {hospitalAnalytics.data?.weekly_growth || 'N/A'}%</li>
                      <li>Peak Hours: {hospitalAnalytics.data?.peak_hours || 'N/A'}</li>
                      <li>Popular Services: {hospitalAnalytics.data?.popular_services || 'N/A'}</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Fleet Tab */}
      {activeTab === 'fleet' && (
        <div className="space-y-6">
          {/* Fleet Profile */}
          <Card>
            <CardHeader>
              <CardTitle>Fleet Profile</CardTitle>
            </CardHeader>
            <CardContent>
              {fleetProfileLoading ? (
                <div>Loading fleet profile...</div>
              ) : fleetProfile ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold">{fleetProfile.data?.name}</h3>
                    <p className="text-sm text-gray-600">{fleetProfile.data?.contact_info}</p>
                    <p className="text-sm text-gray-600">Service Area: {fleetProfile.data?.service_area}</p>
                  </div>
                </div>
              ) : (
                <div>No fleet profile data available</div>
              )}
            </CardContent>
          </Card>

          {/* Fleet Dashboard */}
          <Card>
            <CardHeader>
              <CardTitle>Fleet Dashboard</CardTitle>
            </CardHeader>
            <CardContent>
              {fleetDashboardLoading ? (
                <div>Loading fleet dashboard...</div>
              ) : fleetDashboard ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800">Total Drivers</h4>
                    <p className="text-2xl font-bold text-blue-600">
                      {fleetDashboard.data?.statistics?.total_drivers || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800">Active Drivers</h4>
                    <p className="text-2xl font-bold text-green-600">
                      {fleetDashboard.data?.statistics?.active_drivers || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-800">Total Ambulances</h4>
                    <p className="text-2xl font-bold text-purple-600">
                      {fleetDashboard.data?.statistics?.total_ambulances || 0}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-orange-800">Available Ambulances</h4>
                    <p className="text-2xl font-bold text-orange-600">
                      {fleetDashboard.data?.statistics?.available_ambulances || 0}
                    </p>
                  </div>
                </div>
              ) : (
                <div>No fleet dashboard data available</div>
              )}
            </CardContent>
          </Card>

          {/* Drivers Management */}
          <Card>
            <CardHeader>
              <CardTitle>Drivers Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Add Driver Form */}
                <div className="border-b pb-4">
                  <h4 className="font-medium mb-3">Add New Driver</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      name="name"
                      placeholder="Driver Name"
                      value={formData.name || ''}
                      onChange={handleInputChange}
                    />
                    <Input
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone || ''}
                      onChange={handleInputChange}
                    />
                    <Input
                      name="license_number"
                      placeholder="License Number"
                      value={formData.license_number || ''}
                      onChange={handleInputChange}
                    />
                    <Input
                      name="experience_years"
                      placeholder="Experience (Years)"
                      type="number"
                      value={formData.experience_years || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  <Button 
                    onClick={handleAddDriver}
                    disabled={addingDriver}
                    className="mt-4"
                  >
                    {addingDriver ? 'Adding Driver...' : 'Add Driver'}
                  </Button>
                </div>

                {/* Drivers List */}
                <div>
                  <h4 className="font-medium mb-3">Current Drivers</h4>
                  {driversLoading ? (
                    <div>Loading drivers...</div>
                  ) : drivers && drivers.length > 0 ? (
                    <div className="space-y-2">
                      {drivers.slice(0, 5).map(driver => (
                        <div key={driver.driver_id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                          <div>
                            <span className="font-medium">{driver.name}</span>
                            <span className="text-sm text-gray-600 ml-2">({driver.phone})</span>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            driver.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {driver.status}
                          </span>
                        </div>
                      ))}
                      {drivers.length > 5 && (
                        <Button variant="outline" onClick={loadMoreDrivers}>
                          Load More Drivers
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div>No drivers found</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Booking Tab */}
      {activeTab === 'booking' && (
        <div className="space-y-6">
          {/* Create Booking */}
          <Card>
            <CardHeader>
              <CardTitle>Create New Booking</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="patient_name"
                  placeholder="Patient Name"
                  value={formData.patient_name || ''}
                  onChange={handleInputChange}
                />
                <Input
                  name="patient_phone"
                  placeholder="Patient Phone"
                  value={formData.patient_phone || ''}
                  onChange={handleInputChange}
                />
                <Input
                  name="pickup_address"
                  placeholder="Pickup Address"
                  value={formData.pickup_address || ''}
                  onChange={handleInputChange}
                />
                <Input
                  name="hospital_address"
                  placeholder="Hospital Address"
                  value={formData.hospital_address || ''}
                  onChange={handleInputChange}
                />
                <select
                  name="type"
                  value={formData.type || ''}
                  onChange={handleInputChange}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Booking Type</option>
                  <option value="emergency">Emergency</option>
                  <option value="non_emergency">Non-Emergency</option>
                  <option value="transfer">Transfer</option>
                </select>
                <select
                  name="urgency_level"
                  value={formData.urgency_level || ''}
                  onChange={handleInputChange}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Urgency</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <Button 
                onClick={handleCreateBooking}
                disabled={creatingBooking}
                className="mt-4"
              >
                {creatingBooking ? 'Creating Booking...' : 'Create Booking'}
              </Button>
            </CardContent>
          </Card>

          {/* Recent Bookings */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              {bookingsLoading ? (
                <div>Loading bookings...</div>
              ) : bookings && bookings.length > 0 ? (
                <div className="space-y-3">
                  {bookings.slice(0, 5).map(booking => (
                    <div key={booking.booking_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{booking.patient_name}</h4>
                          <p className="text-sm text-gray-600">{booking.patient_phone}</p>
                          <p className="text-sm text-gray-600">
                            From: {booking.pickup_address}
                          </p>
                          <p className="text-sm text-gray-600">
                            To: {booking.hospital_address}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs ${
                            booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                            booking.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {booking.status}
                          </span>
                          <p className="text-sm text-gray-600 mt-1">
                            {new Date(booking.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>No bookings found</div>
              )}
            </CardContent>
          </Card>

          {/* Booking Analytics */}
          {bookingAnalytics && (
            <Card>
              <CardHeader>
                <CardTitle>Booking Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800">Total Bookings</h4>
                    <p className="text-2xl font-bold text-blue-600">
                      {bookingAnalytics.data?.total_bookings || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800">Revenue</h4>
                    <p className="text-2xl font-bold text-green-600">
                      ₹{bookingAnalytics.data?.total_revenue || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-800">Completion Rate</h4>
                    <p className="text-2xl font-bold text-purple-600">
                      {bookingAnalytics.data?.completion_rate || 0}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* API Integration Info */}
      <Card>
        <CardHeader>
          <CardTitle>🔧 API Integration Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold">Current User Role:</h4>
              <p className="text-sm text-gray-600">{user?.role || 'Unknown'}</p>
            </div>
            <div>
              <h4 className="font-semibold">Available Services:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Hospital Service - Profile, Dashboard, Analytics</li>
                <li>• Fleet Service - Profile, Dashboard, Drivers, Ambulances</li>
                <li>• Booking Service - Create, Manage, Analytics</li>
                <li>• Authentication Service - Login, Registration, Profile</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold">React Hooks Used:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• useHospitalProfile, useHospitalDashboard, useHospitalAnalytics</li>
                <li>• useFleetProfile, useFleetDashboard, useFleetDrivers</li>
                <li>• useBookings, useCreateBooking, useBookingAnalytics</li>
                <li>• useApiMutation for form submissions</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApiIntegrationExample;
